export const ISSUE_TYPE_LABELS: Record<number, string> = {
  0: 'Task',
  1: 'Bug',
  2: 'Story',
  3: 'Epic',
  4: 'Improvement',
  5: 'Sub-task',
  6: 'Feature',
  7: 'Tech Debt',
  8: 'Spike',
  9: 'Requirement',
  10: 'Research',
  11: 'Change Request',
  12: 'Initiative',
  13: 'Portfolio'
};

export const ISSUE_SEVERITY_LABELS: Record<number, string> = {
  0: 'Trivial',
  1: 'Minor',
  2: 'Major',
  3: 'Critical',
  4: 'Blocker'
};

export const ISSUE_RISK_LEVEL_LABELS: Record<number, string> = {
  0: 'Low',
  1: 'Medium',
  2: 'High',
  3: 'Critical'
};

export const ISSUE_STATUS_LABELS: Record<number, string> = {
  0: 'Open',
  1: 'In Progress',
  3: 'Done',
  4: 'Closed',
  5: 'Reopened',
  6: 'Blocked',
  7: 'Overdue',
  8: 'Draft',
  9: 'On Hold',
  10: 'Cancelled',
  11: 'To Do',
  12: 'QA'
};

export const ISSUE_PRIORITY_LABELS: Record<number, string> = {
  0: 'Lowest',
  1: 'Low',
  2: 'Medium',
  3: 'High',
  4: 'Highest'
};

export const DEADLINE_FILTER_OPTIONS = [
  { value: '', label: 'All deadlines' },
  { value: 'dueToday', label: 'Due Today' },
  { value: 'dueTomorrow', label: 'Due Tomorrow' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'completedLate', label: 'Completed Late' },
  { value: 'completedOnTime', label: 'Completed On Time' }
];

export const ISSUE_SORT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'delay', label: 'Delay' },
  { value: 'priority', label: 'Priority' },
  { value: 'progress', label: 'Progress' },
  { value: 'status', label: 'Status' }
];

export const ISSUE_LINK_TYPE_LABELS: Record<number, string> = {
  0: 'Relates to',
  1: 'Blocks',
  2: 'Is blocked by',
  3: 'Duplicates',
  4: 'Is duplicated by',
  5: 'Clones',
  6: 'Is cloned by',
  7: 'Depends on',
  8: 'Is depended on by'
};

export interface IssueWatcherDto {
  userId: string;
  userName: string;
  watchedAt: string;
}

export interface IssueChecklistItemDto {
  id: string;
  text: string;
  isDone: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt?: string;
}

export interface IssueLabelDto {
  id: string;
  projectId: string;
  name: string;
  color?: string;
  description?: string;
}

export interface ProjectComponentDto {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  leadId?: string;
  leadName?: string;
  isActive: boolean;
}

export interface IssueCommentReactionDto {
  emoji: string;
  count: number;
  reactedByMe: boolean;
}

export interface IssueCommentDto {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  isEdited: boolean;
  createdAt: string;
  updatedAt?: string;
  parentCommentId?: string | null;
  reactions?: IssueCommentReactionDto[];
  replies?: IssueCommentDto[];
}

export const ISSUE_LINK_STATUS_LABELS: Record<number, string> = {
  0: 'Active',
  1: 'Resolved',
  2: 'Cancelled'
};

export interface IssueAttachmentDto {
  id: string;
  fileName: string;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  url?: string;
  uploadedById: string;
  uploadedByName?: string;
  createdAt: string;
}

export interface IssueLinkDto {
  id: string;
  sourceIssueId: string;
  sourceIssueKey: string;
  targetIssueId: string;
  targetIssueKey: string;
  targetIssueSummary: string;
  linkType: number;
  linkTypeLabel: string;
  isOutbound: boolean;
  createdAt?: string;
  createdBy?: string;
  direction?: string;
  reason?: string | null;
  status?: number;
  statusLabel?: string;
}

export interface IssueDependenciesDto {
  blockers: IssueLinkDto[];
  blockedBy: IssueLinkDto[];
  dependsOn: IssueLinkDto[];
  dependedOnBy: IssueLinkDto[];
}

export interface IssueHistoryDto {
  id: string;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  changedByUserId?: string;
  changedByName?: string;
  changedAt: string;
}

export interface IssueActivityDto {
  id: string;
  activityType: number;
  activityTypeLabel: string;
  message: string;
  actorUserId?: string;
  actorName?: string;
  metadataJson?: string;
  occurredAt: string;
}

export interface IssueSummaryDto {
  id: string;
  key: string;
  summary: string;
  type: number;
  typeLabel: string;
  status: number;
  statusLabel: string;
  priority: number;
  priorityLabel: string;
  assigneeId?: string;
  assigneeName?: string;
  storyPoints?: number;
}

export interface EpicProgressDto {
  epicId: string;
  epicKey: string;
  epicSummary: string;
  totalChildren: number;
  doneChildren: number;
  percentComplete: number;
  storyPointsCommitted?: number;
  storyPointsDone?: number;
  statusBreakdown?: Record<string, number>;
  overdueCount?: number;
  startDate?: string;
  dueDate?: string;
  color?: string;
}

export interface IssueDto {
  id: string;
  projectId: string;
  projectName: string;
  key: string;
  number: number;
  summary: string;
  description?: string;
  type: number;
  typeLabel: string;
  status: number;
  statusLabel: string;
  priority: number;
  priorityLabel: string;
  assigneeId?: string;
  assigneeName?: string;
  reporterId?: string;
  reporterName?: string;
  parentIssueId?: string;
  parentIssueKey?: string;
  epicId?: string;
  epicKey?: string;
  dueDate?: string;
  startDate?: string;
  storyPoints?: number;
  labels: IssueLabelDto[];
  components: ProjectComponentDto[];
  commentCount: number;
  attachmentCount: number;
  subtaskCount: number;
  createdAt: string;
  updatedAt?: string;
  actualStartDate?: string;
  actualCompletionDate?: string;
  isOverdue?: boolean;
  delayInDays?: number;
  delayLabel?: string;
  daysRemaining?: number | null;
  remainingLabel?: string;
  completedOnTime?: boolean | null;
  deadlineBadge?: string;
  completionStatusLabel?: string;
  escalationLevel?: number;
  escalationLabel?: string;
  riskLevel?: string | number;
  acceptanceCriteria?: string;
  definitionOfDone?: string;
  severity?: number;
  actualHours?: number;
  progressPercent?: number;
  color?: string;
  archivedAt?: string | null;
}

export interface IssueDetailDto extends IssueDto {
  originalEstimateHours?: number;
  remainingEstimateHours?: number;
  environment?: string;
  resolvedAt?: string;
  comments: IssueCommentDto[];
  attachments: IssueAttachmentDto[];
  links: IssueLinkDto[];
  subtasks: IssueSummaryDto[];
  checklistItems?: IssueChecklistItemDto[];
  epicProgress?: EpicProgressDto;
  projectHealth?: string;
  isWatching?: boolean;
}

export interface CreateIssueRequest {
  projectId: string;
  summary: string;
  description?: string;
  type?: number;
  priority?: number;
  assigneeId?: string;
  parentIssueId?: string;
  epicId?: string;
  dueDate?: string;
  startDate?: string;
  storyPoints?: number;
  originalEstimateHours?: number;
  acceptanceCriteria?: string;
  definitionOfDone?: string;
  severity?: number;
  riskLevel?: number;
  actualHours?: number;
  environment?: string;
  labelIds?: string[];
  componentIds?: string[];
  color?: string;
}

export interface UpdateIssueRequest {
  summary: string;
  description?: string;
  type: number;
  status: number;
  priority: number;
  assigneeId?: string;
  parentIssueId?: string;
  epicId?: string;
  dueDate?: string;
  startDate?: string;
  storyPoints?: number;
  originalEstimateHours?: number;
  remainingEstimateHours?: number;
  acceptanceCriteria?: string;
  definitionOfDone?: string;
  severity?: number;
  riskLevel?: number;
  actualHours?: number;
  environment?: string;
  labelIds?: string[];
  componentIds?: string[];
  color?: string;
}

export interface IssueListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  projectId?: string;
  type?: number;
  status?: number;
  priority?: number;
  assigneeId?: string;
  labelId?: string;
  componentId?: string;
  epicId?: string;
  parentIssueId?: string;
  deadlineFilter?: string;
  sortBy?: string;
  includeArchived?: boolean;
}

export interface CloneIssueRequest {
  copyChecklist?: boolean;
  copyLinks?: boolean;
  copySubtasks?: boolean;
  summarySuffix?: string;
  createClonesLink?: boolean;
}

export interface BulkUpdateIssuesRequest {
  issueIds: string[];
  status?: number;
  assigneeId?: string;
  epicId?: string;
  sprintId?: string;
  priority?: number;
  delete?: boolean;
  archive?: boolean;
  unassign?: boolean;
}

export interface BulkUpdateIssuesResult {
  updatedCount: number;
  updatedIssueIds: string[];
}

export interface AddIssueAttachmentRequest {
  fileName: string;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  storagePath: string;
  url?: string;
  storedFileId?: string;
}

export interface CreateIssueLinkRequest {
  targetIssueId: string;
  linkType?: number;
  reason?: string | null;
  status?: number;
}

export interface UpdateIssueLinkRequest {
  reason?: string | null;
  status?: number | null;
}

export interface CreateSubtaskRequest {
  summary: string;
  description?: string;
  priority?: number;
  assigneeId?: string;
}
